import { Box, Stack, Typography } from '@mui/material'
import { C1IconButton } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { Searchable } from 'components/core/autocomplete'
import { EndUserTaskListItemSkeleton } from 'components/core/end-user-task-list/EndUserTaskList'
import { MultiAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { useMultiReduxHook } from 'hooks/useMultiReduxHook/useMultiReduxHook'
import { Route } from 'lib/routes'
import { RefreshCw } from 'lucide-react'
import { FC, ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { HookLike } from 'redux_toolkit/lib/createPagedListApi'
import { InvalidateHookLike, UnaryHookLike } from 'redux_toolkit/lib/createUnaryApi'

/**
 * Conditional type that checks if type T has an id property
 */
type HasId<T> = T extends { id: string } ? true : false

/**
 * Props for the EndUserDashboardSection component with conditional getId based on T type
 * @template T The type of items in the list
 */
type EndUserDashboardSectionProps<T extends object, ReqData, ReqCount> = {
    /** The main title of the section */
    title: string
    /** The secondary text displayed below the title */
    subtitle?: string
    /** URL for the "See all" or similar navigation link */
    seeAllHref: Route
    /** Label for the "See all" or similar navigation link */
    seeAllLabel: (count: number) => string
    /** Component to render each item in the list */
    ListItemComponent: FC<{ row: T }>
    /** Component to render each item in the list */
    ListItemSkeleton?: FC
    /** Hook to fetch the list of items */
    getDataHook: HookLike<ReqData, T>
    /** Request object for the hook */
    getDataRequest: ReqData
    /** Hook to fetch the count of items */
    getCountHook: UnaryHookLike<ReqCount, { count: number }>
    /** Request object for the count hook */
    getCountRequest: ReqCount
    /** Whether to show the loading state */
    showIsLoading?: boolean
    /** Hook to invalidate the list */
    invalidateHook: InvalidateHookLike
    /** Whether to display the items horizontally */
    isHorizontal?: boolean
} & (
        /**
         * If T has an id property, use the getId function to get the id
         * Otherwise, use the default implementation
         */
        HasId<T> extends true
        ? { getId?: (item: T) => string }
        : { getId: (item: T) => string }
    )

/**
 * Displays a short list of items with a header, and a link to see "all"
 *
 * @template T The type of items in the list
 * @param {EndUserDashboardSectionProps<T>} props - The component props
 * @returns {ReactNode} The rendered dashboard section
 */
export const EndUserDashboardSection = <T extends object, ReqData, ReqCount>({
    title,
    subtitle,
    ListItemComponent,
    seeAllHref,
    seeAllLabel,
    getId,
    getDataHook: useHook,
    getDataRequest,
    getCountHook: useCountHook,
    getCountRequest,
    ListItemSkeleton = EndUserTaskListItemSkeleton,
    showIsLoading = false,
    invalidateHook: useInvalidateHook,
    isHorizontal = false,
}: EndUserDashboardSectionProps<T, ReqData, ReqCount>): ReactNode => {
    const intl = useIntl()
    const getItemId = getId || ((item: T): string => (item as unknown as Searchable).id)

    const invalidateList = useInvalidateHook()

    const {
        data: [data, countData],
        isLoading,
        errors,
        shouldInvalidateList,
    } = useMultiReduxHook([
        useHook(getDataRequest, { pageSize: 3, notify: true }),
        useCountHook(getCountRequest, { notify: true }),
    ])

    if (showIsLoading && isLoading) {
        return <Stack justifyContent="center" alignItems="center" height="30vh">
            <C1CircularProgress />
        </Stack>
    }

    if (isLoading && !data) return null

    return data && countData && countData.count > 0 && (
        <Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                <Stack>
                    <Stack direction="row" alignItems="center">
                        <Typography variant="h4" component="div">{title}</Typography>
                        {shouldInvalidateList && <C1IconButton
                            onClick={invalidateList}
                            aria-label={intl.formatMessage({
                                id: 'components_end_user_dashboard_refresh_list',
                                defaultMessage: 'Refresh list',
                            })}
                        >
                            <C1Icon icon={RefreshCw} />
                        </C1IconButton>}
                    </Stack>
                    {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
                </Stack>
                <C1Link href={seeAllHref}>
                    {seeAllLabel(countData.count)}
                </C1Link>
            </Stack>
            <MultiAPIError errs={errors} />
            <Box sx={isHorizontal ? {
                mt: 3,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                gap: 3,
            } : {
                mt: 3,
                display: 'flex',
                flexDirection: 'column',
                '& > :first-of-type': {
                    borderColor: 'border.subtle',
                    borderTopLeftRadius: C1BorderRadius.large,
                    borderTopRightRadius: C1BorderRadius.large,
                },
                '& > :last-of-type': {
                    borderColor: 'border.subtle',
                    borderBottomLeftRadius: C1BorderRadius.large,
                    borderBottomRightRadius: C1BorderRadius.large,
                },
            }}
            >
                {isLoading
                    ? Array.from({ length: 3 }).map((_, i) => <ListItemSkeleton key={i} />)
                    : data.flat().slice(0, 3).map((row) => <ListItemComponent row={row} key={getItemId(row)} />)}
            </Box>
        </Stack>
    )
}
